import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { GoogleLogo, ShieldCheck, EnvelopeSimple, Key } from "@phosphor-icons/react";

export default function AuthPage({ mode = "login" }) {
    const { t, login, register } = useApp();
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const referralCode = sp.get("ref") || "";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);

    const isLogin = mode === "login";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isLogin) {
                await login(email.toLowerCase(), password);
                toast.success(t("dashboard.youAreProtected"));
            } else {
                await register({
                    email: email.toLowerCase(),
                    password,
                    name,
                    referral_code: referralCode || null,
                });
                toast.success(t("auth.createAccount"));
            }
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Fehler / Error");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        // Preserve referral in a session-storage key so AuthCallback can attach it
        if (referralCode) sessionStorage.setItem("legashield_ref", referralCode);
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <AppShell>
            <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-14 items-start">
                <div className="hidden lg:block">
                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                        {t("landing.eyebrow")}
                    </div>
                    <h1 className="mt-3 font-heading font-bold text-shield-ink text-4xl lg:text-5xl leading-tight">
                        {isLogin ? t("auth.welcomeBack") : t("auth.createAccount")}
                    </h1>
                    <p className="mt-4 text-lg text-shield-muted leading-relaxed max-w-lg">
                        {isLogin ? t("auth.loginSub") : t("auth.registerSub")}
                    </p>
                    <div className="mt-10 space-y-3">
                        {t("landing.securityPoints").map((p) => (
                            <div key={p} className="flex items-center gap-3">
                                <ShieldCheck size={20} weight="duotone" className="text-shield-forest" aria-hidden="true" />
                                <span className="text-shield-ink font-medium">{p}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="card-soft p-8 sm:p-10" data-testid="auth-card">
                    <h2 className="lg:hidden font-heading font-bold text-2xl text-shield-ink">
                        {isLogin ? t("auth.welcomeBack") : t("auth.createAccount")}
                    </h2>
                    <p className="lg:hidden text-shield-muted mt-1">
                        {isLogin ? t("auth.loginSub") : t("auth.registerSub")}
                    </p>

                    <button
                        onClick={handleGoogle}
                        className="mt-6 lg:mt-0 w-full inline-flex items-center justify-center gap-3 h-14 rounded-xl border-2 border-shield-line bg-white hover:border-shield-forest text-shield-ink font-semibold transition-colors"
                        data-testid="google-btn"
                    >
                        <GoogleLogo size={22} weight="bold" aria-hidden="true" />
                        {t("auth.googleBtn")}
                    </button>

                    <div className="flex items-center gap-3 my-6">
                        <div className="flex-1 h-px bg-shield-line" />
                        <span className="text-xs uppercase tracking-[0.15em] text-shield-muted font-semibold">
                            {t("auth.or")}
                        </span>
                        <div className="flex-1 h-px bg-shield-line" />
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
                        {!isLogin && (
                            <div>
                                <label className="block text-sm font-semibold text-shield-ink mb-1.5">
                                    {t("auth.name")}
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full h-14 rounded-xl border-2 border-shield-line px-4 text-lg focus:border-shield-forest focus:outline-none focus:ring-4 focus:ring-shield-forest/20 transition-colors bg-white"
                                    placeholder="Anna Müller"
                                    data-testid="name-input"
                                />
                            </div>
                        )}
                        <div>
                            <label className="flex items-center gap-1.5 text-sm font-semibold text-shield-ink mb-1.5">
                                <EnvelopeSimple size={14} aria-hidden="true" /> {t("auth.email")}
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full h-14 rounded-xl border-2 border-shield-line px-4 text-lg focus:border-shield-forest focus:outline-none focus:ring-4 focus:ring-shield-forest/20 transition-colors bg-white"
                                placeholder="du@example.com"
                                data-testid="email-input"
                            />
                        </div>
                        <div>
                            <label className="flex items-center gap-1.5 text-sm font-semibold text-shield-ink mb-1.5">
                                <Key size={14} aria-hidden="true" /> {t("auth.password")}
                            </label>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full h-14 rounded-xl border-2 border-shield-line px-4 text-lg focus:border-shield-forest focus:outline-none focus:ring-4 focus:ring-shield-forest/20 transition-colors bg-white"
                                placeholder="min. 8 Zeichen"
                                data-testid="password-input"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-14 pill bg-shield-forest text-white text-lg font-semibold hover:bg-shield-forestDeep transition-colors active:scale-[0.98] disabled:opacity-70"
                            data-testid="auth-submit"
                        >
                            {loading ? t("common.loading") : (isLogin ? t("auth.login") : t("auth.register"))}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-shield-muted">
                        {isLogin ? t("auth.noAccount") : t("auth.hasAccount")}{" "}
                        <Link
                            to={isLogin ? "/register" : "/login"}
                            className="text-shield-forest font-semibold hover:underline"
                            data-testid="auth-switch-link"
                        >
                            {isLogin ? t("auth.registerHere") : t("auth.loginHere")}
                        </Link>
                    </p>
                </div>
            </div>
        </AppShell>
    );
}
